import React, { useRef } from "react";
import { marked } from "marked";
import { EditorHeader } from "./EditorHeader";
import { EditorSettingsPanel } from "./EditorSettingsPanel";
import { MarkdownToolbar } from "./MarkdownToolbar";
import { useBlogEditor } from "./useBlogEditor";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Eye, EyeOff } from "lucide-react";

import { VersionDiffViewer } from "../VersionDiffViewer";
import { AlertCircle, Trash2, Save } from "lucide-react";

export interface BlogPost {
  slug: string;
  title_en: string;
  title_tr: string;
  date: string;
  description_en: string;
  description_tr: string;
  tags_en: string;
  tags_tr: string;
  content_en: string;
  content_tr: string;
  published?: number;
  categories?: string;
}

interface Category {
  id: string;
  name_en: string;
  name_tr: string;
}

interface Tag {
  id: string;
  name_en: string;
  name_tr: string;
}

interface BlogEditorPageProps {
  blogs: BlogPost[];
  categories: Category[];
  tags: Tag[];
  onRefresh: () => void;
}

function EditorContentArea({
  textareaRef,
  activeContent,
  handleContentChange,
  onSave,
  saving,
  showPreview,
  setShowPreview,
  activeLang,
  setActiveLang
}: any) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center text-left">
        <Label className="text-xs font-bold text-brand-primary uppercase tracking-wider">
          {activeLang === "en" ? "English Content (Markdown)" : "Türkçe İçerik (Markdown)"}
        </Label>

        <div className="flex items-center gap-3">
          <div className="flex border border-border/60 rounded-xl overflow-hidden bg-muted/20 p-0.5">
            <button
              type="button"
              onClick={() => setActiveLang("en")}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${activeLang === "en" ? "bg-brand-primary text-white" : "text-muted-foreground hover:text-foreground"
                }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setActiveLang("tr")}
              className={`px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all ${activeLang === "tr" ? "bg-brand-primary text-white" : "text-muted-foreground hover:text-foreground"
                }`}
            >
              TR
            </button>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs text-brand-secondary hover:text-brand-primary gap-1"
          >
            {showPreview ? <><EyeOff className="h-3.5 w-3.5" /> Hide Preview</> : <><Eye className="h-3.5 w-3.5" /> Live Preview</>}
          </Button>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${showPreview ? "lg:grid-cols-2" : ""} gap-6 items-start`}>
        <div className="border border-border/60 rounded-xl bg-card">
          <MarkdownToolbar
            textareaRef={textareaRef}
            content={activeContent}
            onContentChange={handleContentChange}
            onSave={onSave}
            saving={saving}
          />
          <textarea
            ref={textareaRef}
            rows={15}
            placeholder={activeLang === "en" ? "Start writing in markdown..." : "Markdown ile yazmaya başlayın..."}
            className="w-full p-4 font-mono text-sm border-0 bg-transparent text-foreground focus:outline-none resize-y min-h-[300px] rounded-b-xl"
            value={activeContent}
            onChange={(e) => handleContentChange(e.target.value)}
          />
        </div>

        {showPreview && (
          <div className="border border-border/60 rounded-xl bg-card/30 p-5 h-[354px] lg:h-[370px] overflow-y-auto custom-scrollbar prose dark:prose-invert max-w-none text-left">
            <div
              dangerouslySetInnerHTML={{
                __html: marked.parse(activeContent || "*No content to preview*") as string,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogEditorPage({ blogs, categories, tags, onRefresh }: BlogEditorPageProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editor = useBlogEditor(blogs, categories, tags, onRefresh);

  const livePost = blogs.find((b) => b.slug === editor.slug);
  const diffFields = [
    { key: "title_en", label: "English Title" },
    { key: "title_tr", label: "Turkish Title" },
    { key: "description_en", label: "English Description", isLongText: true },
    { key: "description_tr", label: "Turkish Description", isLongText: true },
    { key: "content_en", label: "English Content (EN)", isLongText: true },
    { key: "content_tr", label: "Turkish Content (TR)", isLongText: true },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12 text-left">
      <EditorHeader
        title={editor.activeLang === "en" ? editor.titleEn : editor.titleTr}
        isPublished={editor.published === 1}
        onSave={editor.handleSave}
        onPublishToggle={editor.handlePublishToggle}
        saving={editor.saving}
        publishing={editor.publishing}
      />

      {/* Staging Actions Panel */}
      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {editor.activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {editor.activeDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={editor.handleDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={editor.handleSaveDraft}
            disabled={editor.saving}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
          </Button>
        </div>
      </div>

      <EditorSettingsPanel
        slug={editor.slug} setSlug={editor.setSlug}
        date={editor.date} setDate={editor.setDate}
        titleEn={editor.titleEn} setTitleEn={editor.setTitleEn}
        titleTr={editor.titleTr} setTitleTr={editor.setTitleTr}
        summaryEn={editor.summaryEn} setSummaryEn={editor.setSummaryEn}
        summaryTr={editor.summaryTr} setSummaryTr={editor.setSummaryTr}
        selectedCategories={editor.selectedCategories} setSelectedCategories={editor.setSelectedCategories}
        selectedTags={editor.selectedTags} setSelectedTags={editor.setSelectedTags}
        categories={categories} tags={tags}
        activeLang={editor.activeLang} isSlugDisabled={editor.isSlugDisabled}
      />

      <EditorContentArea
        textareaRef={textareaRef}
        activeContent={editor.activeContent}
        handleContentChange={editor.handleContentChange}
        onSave={editor.handleSave}
        saving={editor.saving}
        showPreview={editor.showPreview}
        setShowPreview={editor.setShowPreview}
        activeLang={editor.activeLang}
        setActiveLang={editor.setActiveLang}
      />

      {livePost && (
        <VersionDiffViewer
          live={livePost}
          draft={editor.getPayload()}
          fieldsToCompare={diffFields}
        />
      )}
    </div>
  );
}
export default BlogEditorPage;
